<script>
    const norm = value => (value || '').trim().toLowerCase().replace(/\s+/g, ' ');

    const same = (a, b) => norm(a) === norm(b) && norm(a) !== '';

    const kindOf = item => (item.kind || '').toLowerCase();

    const productOf = item => (item.product || '').toLowerCase();

    const ICONS = {
        Standard: '/assets/images/direct-mode/battery.svg',
        CollectionPoint: '/assets/images/direct-mode/collectionPoint.svg',
        SWD: '/assets/images/direct-mode/battery-swd.svg',
        Well: '/assets/images/direct-mode/well.svg',
        'Oil tank': '/assets/images/direct-mode/oiltank.svg',
        'Water tank': '/assets/images/direct-mode/watertank.svg',
        'Oil meter': '/assets/images/direct-mode/lact.svg',
        'Water meter': '/assets/images/direct-mode/watermeter.svg',
        'Gas meter': '/assets/images/direct-mode/gasmeter.svg'
    };

    export default {
        name: 'PlanShell',
        props: {
            facilities: { type: Array, default: () => [] },
            people: { type: Array, default: () => [] },
            groups: { type: Array, default: () => [] },
            query: { type: String, default: '' }
        },
        data() {
            return {
                groupName: '',
                selectedName: '',
                selectedTab: 'overview',
                openNames: []
            };
        },
        computed: {
            selectedGroup() {
                return this.groups.find(g => g.name === this.groupName) || null;
            },
            inGroup() {
                const names = (this.selectedGroup?.facilities || []).map(norm);
                if (!this.selectedGroup) return () => true;
                return facility => names.includes(norm(facility.name));
            },
            tree() {
                const named = new Set(this.facilities.map(f => norm(f.name)));
                const isChild = facility =>
                    facility.collectionPoint &&
                    named.has(norm(facility.collectionPoint)) &&
                    !same(facility.collectionPoint, facility.name);
                const parentOf = facility => this.facilities.find(f => same(f.name, facility.collectionPoint));
                const childrenOf = parent => this.facilities.filter(f => isChild(f) && same(f.collectionPoint, parent.name) && this.inGroup(f));
                const top = this.facilities.filter(facility => {
                    if (!this.inGroup(facility)) return false;
                    if (!isChild(facility)) return true;
                    const parent = parentOf(facility);
                    return !parent || !this.inGroup(parent);
                });
                const query = norm(this.query);
                return top
                    .map(facility => {
                        const node = this.listed(facility);
                        const children = childrenOf(facility)
                            .map(child => this.listed(child))
                            .filter(child => !query || norm(child.facility.name).includes(query) || child.assets.length);
                        return { ...node, children };
                    })
                    .filter(node => !query || norm(node.facility.name).includes(query) || node.assets.length || node.children.length);
            },
            selected() {
                return this.facilities.find(f => same(f.name, this.selectedName)) || this.tree[0]?.facility || null;
            },
            tabs() {
                const facility = this.selected;
                if (!facility) return [];
                const tabs = [{ id: 'overview', label: 'Overview' }];
                if (this.byKind(facility, 'gas').length) tabs.push({ id: 'gas', label: 'MCF & Facility Equipment' });
                if (this.byProduct(facility, 'oil').length || this.byKind(facility, 'oil').length) tabs.push({ id: 'oil', label: 'Oil' });
                if (this.byProduct(facility, 'water').length || this.byKind(facility, 'water').length) {
                    tabs.push({ id: 'water', label: 'Water / Meter' });
                }
                if ((facility.wells || []).length) {
                    tabs.push({
                        id: 'wells',
                        label: facility.type === 'CollectionPoint' ? 'Allocations' : 'Wells'
                    });
                }
                return tabs;
            },
            viewers() {
                const facility = this.selected;
                if (!facility) return [];
                return this.people.filter(p => (p.facilities || []).some(name => same(name, facility.name)));
            },
            duplicateKeys() {
                const counts = {};
                const bump = (kind, facility, name) => {
                    const key = `${kind}|${norm(facility)}|${norm(name)}`;
                    counts[key] = (counts[key] || 0) + 1;
                };
                for (const facility of this.facilities) {
                    for (const well of facility.wells || []) bump('well', facility.name, well.name);
                    for (const tank of facility.tanks || []) bump(`tank-${productOf(tank)}`, facility.name, tank.name);
                    for (const meter of facility.meters || []) bump(`meter-${kindOf(meter)}`, facility.name, meter.name);
                }
                return new Set(Object.keys(counts).filter(key => counts[key] > 1));
            }
        },
        methods: {
            assetsOf(facility) {
                const rows = [];
                const push = (prefix, item, tab) => rows.push({ prefix, item, tab, facility: facility.name });
                for (const well of facility.wells || []) push('Well', well, 'wells');
                for (const tank of facility.tanks || []) {
                    if (productOf(tank) === 'water') push('Water tank', tank, 'water');
                    else push('Oil tank', tank, 'oil');
                }
                for (const meter of facility.meters || []) {
                    const kind = kindOf(meter);
                    if (kind === 'gas') push('Gas meter', meter, 'gas');
                    else if (kind === 'water') push('Water meter', meter, 'water');
                    else push('Oil meter', meter, 'oil');
                }
                return rows;
            },
            byProduct(facility, product) {
                return (facility.tanks || []).filter(t => productOf(t) === product);
            },
            byKind(facility, kind) {
                return (facility.meters || []).filter(m => kindOf(m) === kind);
            },
            sameName(a, b) {
                return same(a, b);
            },
            containerNames(name) {
                const facility = this.facilities.find(f => same(f.name, name));
                if (!facility) return [];
                const parent = this.facilities.find(f => same(f.name, facility.collectionPoint) && !same(f.name, facility.name));
                return parent ? [parent.name, facility.name] : [facility.name];
            },
            typeLabel(type) {
                return type === 'CollectionPoint' ? 'Collection point' : type || '';
            },
            listed(facility) {
                const query = norm(this.query);
                const facilityHit = !query || norm(facility.name).includes(query);
                const assets = this.assetsOf(facility).filter(row =>
                    facilityHit || [row.item.name, row.item.apiNumber].some(value => norm(value).includes(query)));
                return { facility, assets };
            },
            isOpen(name) {
                if (norm(this.query)) return true;
                return this.openNames.some(open => same(open, name));
            },
            toggleOpen(name) {
                this.openNames = this.isOpen(name)
                    ? this.openNames.filter(open => !same(open, name))
                    : this.openNames.concat(name);
            },
            openFacility(name) {
                if (!this.isOpen(name)) this.openNames = this.openNames.concat(name);
            },
            selectFacility(name) {
                this.selectedName = name;
                this.openFacility(name);
                if (!this.tabs.some(tab => tab.id === this.selectedTab)) this.selectedTab = 'overview';
            },
            selectAsset(row) {
                this.selectedName = row.facility;
                this.selectedTab = row.tab;
                this.openFacility(row.facility);
            },
            isDuplicate(kind, facility, name) {
                return this.duplicateKeys.has(`${kind}|${norm(facility)}|${norm(name)}`);
            },
            wellRecord(well) {
                const record = well.wellDb || {};
                return Object.keys(record)
                    .filter(key => record[key] != null && record[key] !== '')
                    .map(key => ({
                        key,
                        label: key.replace(/[A-Z]/g, letter => ` ${letter.toLowerCase()}`),
                        value: record[key],
                        filled: (well.filledFromWellDb || []).includes(key)
                    }));
            },
            statusClass(status) {
                if (status === 'Existing') return 'bg-secondary';
                if (status === 'Missing') return 'bg-danger';
                return 'bg-success';
            },
            facilityIcon(type) {
                return ICONS[type] || ICONS.Standard;
            },
            assetIcon(prefix) {
                return ICONS[prefix] || ICONS.Standard;
            }
        },
        watch: {
            facilities: {
                immediate: true,
                handler(facilities) {
                    const stillThere = (facilities || []).some(f => same(f.name, this.selectedName));
                    if (!stillThere) {
                        this.selectedName = facilities?.[0]?.name || '';
                        this.selectedTab = 'overview';
                        this.openNames = this.containerNames(this.selectedName);
                    }
                }
            },
            groupName() {
                const visible = this.tree.flatMap(node => [node.facility.name, ...node.children.map(child => child.facility.name)]);
                if (visible.length && !visible.some(name => same(name, this.selectedName))) this.selectFacility(visible[0]);
            }
        }
    };
</script>

<template>
    <div v-if="selected" class="plan-shell">
        <div class="plan-shell-nav">
            <div class="plan-shell-sidehead">Facilities</div>
            <div class="plan-shell-filters">
                <label v-if="groups.length" class="plan-shell-group-wrap">
                    <span class="visually-hidden">Group</span>
                    <select v-model="groupName" class="plan-shell-group" aria-label="Group">
                        <option value="">All groups</option>
                        <option v-for="group in groups" :key="group.name" :value="group.name">{{ group.name }}</option>
                    </select>
                </label>
            </div>
            <div v-for="node in tree" :key="node.facility.name">
                <button
                    type="button"
                    class="plan-shell-row"
                    :class="{ 'is-selected': selected && sameName(selected.name, node.facility.name) }"
                    @click="selectFacility(node.facility.name)"
                >
                    <span
                        v-if="node.children.length || node.assets.length"
                        class="plan-shell-twist"
                        :class="{ 'is-open': isOpen(node.facility.name) }"
                        @click.stop="toggleOpen(node.facility.name)"
                    ></span>
                    <img class="plan-shell-icon" :src="facilityIcon(node.facility.type)" alt="" width="20" height="20" />
                    <span class="plan-shell-name">{{ node.facility.name }}</span>
                </button>
                <template v-if="isOpen(node.facility.name)">
                    <button
                        v-for="(row, index) in node.assets"
                        :key="node.facility.name + row.prefix + index"
                        type="button"
                        class="plan-shell-row plan-shell-asset"
                        @click="selectAsset(row)"
                    >
                        <img class="plan-shell-icon" :src="assetIcon(row.prefix)" alt="" width="20" height="20" />
                        <span class="plan-shell-name">{{ row.item.name }}</span>
                    </button>
                    <div v-for="child in node.children" :key="child.facility.name">
                        <button
                            type="button"
                            class="plan-shell-row plan-shell-child"
                            :class="{ 'is-selected': selected && sameName(selected.name, child.facility.name) }"
                            @click="selectFacility(child.facility.name)"
                        >
                            <img class="plan-shell-icon" :src="facilityIcon(child.facility.type)" alt="" width="20" height="20" />
                            <span class="plan-shell-name">{{ child.facility.name }}</span>
                        </button>
                        <button
                            v-for="(row, index) in child.assets"
                            :key="child.facility.name + row.prefix + index"
                            type="button"
                            class="plan-shell-row plan-shell-asset plan-shell-nested"
                            @click="selectAsset(row)"
                        >
                            <img class="plan-shell-icon" :src="assetIcon(row.prefix)" alt="" width="20" height="20" />
                            <span class="plan-shell-name">{{ row.item.name }}</span>
                        </button>
                    </div>
                </template>
            </div>
        </div>
        <div class="plan-shell-main">
            <div class="plan-shell-tabs" role="tablist">
                <button
                    v-for="tab in tabs"
                    :key="tab.id"
                    type="button"
                    role="tab"
                    :aria-selected="selectedTab === tab.id"
                    :class="{ 'is-selected': selectedTab === tab.id }"
                    @click="selectedTab = tab.id"
                >
                    {{ tab.label }}
                </button>
            </div>
            <div class="plan-shell-body" role="tabpanel">
                <div class="plan-shell-title">
                    <h3>{{ selected.name }}</h3>
                    <span class="plan-shell-type">{{ typeLabel(selected.type) }}</span>
                    <span v-if="selected.status" class="badge rounded-pill fw-normal" :class="statusClass(selected.status)">
                        {{ selected.status }}
                    </span>
                </div>
                <template v-if="selectedTab === 'overview'">
                    <p v-if="selected.state || selected.county || selected.collectionPoint" class="plan-shell-meta">
                        {{ [selected.county, selected.state].filter(Boolean).join(', ') }}
                        <template v-if="selected.collectionPoint"> · {{ selected.collectionPoint }}</template>
                    </p>
                    <div v-if="viewers.length">
                        <div class="plan-shell-heading">Sees this facility</div>
                        <div v-for="person in viewers" :key="person.name" class="plan-shell-line">
                            {{ person.name }}<template v-if="person.role"> · {{ person.role }}</template>
                        </div>
                    </div>
                </template>
                <template v-if="selectedTab === 'gas'">
                    <div class="plan-shell-heading">Gas meters</div>
                    <div v-for="(meter, index) in byKind(selected, 'gas')" :key="meter.name + index" class="plan-shell-line">
                        <img class="plan-shell-icon" :src="assetIcon('Gas meter')" alt="" width="20" height="20" />
                        <span>{{ meter.name }}<template v-if="meter.type"> · {{ meter.type }}</template></span>
                        <span v-if="isDuplicate('meter-gas', selected.name, meter.name)" class="badge bg-warning text-dark">Possible duplicate</span>
                    </div>
                </template>
                <template v-if="selectedTab === 'oil'">
                    <template v-if="byProduct(selected, 'oil').length">
                        <div class="plan-shell-heading">Oil tanks</div>
                        <div v-for="(tank, index) in byProduct(selected, 'oil')" :key="tank.name + index" class="plan-shell-line">
                            <img class="plan-shell-icon" :src="assetIcon('Oil tank')" alt="" width="20" height="20" />
                            <span>{{ tank.name }}</span>
                            <span v-if="isDuplicate('tank-oil', selected.name, tank.name)" class="badge bg-warning text-dark">Possible duplicate</span>
                        </div>
                    </template>
                    <template v-if="byKind(selected, 'oil').length">
                        <div class="plan-shell-heading">Oil meters</div>
                        <div v-for="(meter, index) in byKind(selected, 'oil')" :key="meter.name + index" class="plan-shell-line">
                            <img class="plan-shell-icon" :src="assetIcon('Oil meter')" alt="" width="20" height="20" />
                            <span>{{ meter.name }}<template v-if="meter.type"> · {{ meter.type }}</template></span>
                            <span v-if="isDuplicate('meter-oil', selected.name, meter.name)" class="badge bg-warning text-dark">Possible duplicate</span>
                        </div>
                    </template>
                </template>
                <template v-if="selectedTab === 'water'">
                    <template v-if="byProduct(selected, 'water').length">
                        <div class="plan-shell-heading">Water tanks</div>
                        <div v-for="(tank, index) in byProduct(selected, 'water')" :key="tank.name + index" class="plan-shell-line">
                            <img class="plan-shell-icon" :src="assetIcon('Water tank')" alt="" width="20" height="20" />
                            <span>{{ tank.name }}</span>
                            <span v-if="isDuplicate('tank-water', selected.name, tank.name)" class="badge bg-warning text-dark">Possible duplicate</span>
                        </div>
                    </template>
                    <template v-if="byKind(selected, 'water').length">
                        <div class="plan-shell-heading">Water meters</div>
                        <div v-for="(meter, index) in byKind(selected, 'water')" :key="meter.name + index" class="plan-shell-line">
                            <img class="plan-shell-icon" :src="assetIcon('Water meter')" alt="" width="20" height="20" />
                            <span>{{ meter.name }}<template v-if="meter.type"> · {{ meter.type }}</template></span>
                            <span v-if="isDuplicate('meter-water', selected.name, meter.name)" class="badge bg-warning text-dark">Possible duplicate</span>
                        </div>
                    </template>
                </template>
                <template v-if="selectedTab === 'wells'">
                    <div class="plan-shell-heading">{{ selected.type === 'CollectionPoint' ? 'Allocations' : 'Wells' }}</div>
                    <div v-for="(well, index) in selected.wells" :key="well.name + index" class="plan-shell-line plan-shell-well">
                        <div class="plan-shell-line-main">
                            <img class="plan-shell-icon" :src="assetIcon('Well')" alt="" width="20" height="20" />
                            <span>
                                {{ well.name }}
                                <template v-if="well.apiNumber"> · API {{ well.apiNumber }}</template>
                            </span>
                            <span v-if="isDuplicate('well', selected.name, well.name)" class="badge bg-warning text-dark">Possible duplicate</span>
                        </div>
                        <div v-for="field in wellRecord(well)" :key="field.key" class="plan-shell-db">
                            {{ field.label }}: {{ field.value }}<template v-if="field.filled"> · from well database</template>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @font-face {
        font-family: Poppins;
        font-style: normal;
        font-weight: 400;
        font-display: swap;
        src: url('/assets/fonts/poppins/poppins-latin-400.woff2') format('woff2');
    }

    @font-face {
        font-family: Poppins;
        font-style: normal;
        font-weight: 600;
        font-display: swap;
        src: url('/assets/fonts/poppins/poppins-latin-600.woff2') format('woff2');
    }

    @font-face {
        font-family: Poppins;
        font-style: normal;
        font-weight: 700;
        font-display: swap;
        src: url('/assets/fonts/poppins/poppins-latin-700.woff2') format('woff2');
    }

    .plan-shell {
        --bs-primary: #23a89e;
        --bs-primary-rgb: 35, 168, 158;
        display: flex;
        min-height: 28rem;
        margin: 0 -1rem -1rem;
        border-top: 1px solid var(--bs-border-color);
        background: #fff;
        color: var(--bs-body-color);
        font-family: Poppins, sans-serif;
        overflow: hidden;
    }

    .plan-shell-nav {
        width: 22rem;
        flex-shrink: 0;
        border-right: 1px solid var(--bs-border-color);
        background: #fff;
        max-height: 40rem;
        overflow: auto;
    }

    .plan-shell-sidehead {
        padding: 0.85rem 1rem 0.35rem;
        color: #8b919a;
        font-size: 0.72rem;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
    }

    .plan-shell-filters {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
        padding: 0.15rem 0.85rem 0.65rem;
    }

    .plan-shell-group-wrap {
        display: block;
        margin: 0;
    }

    .plan-shell-group {
        width: 100%;
        border: 0;
        background: transparent;
        color: inherit;
        font: inherit;
        font-size: 0.95rem;
        padding: 0.2rem 0;
        outline: none;
        cursor: pointer;
    }

    .plan-shell-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        width: 100%;
        margin: 0;
        padding: 0.15rem 0.75rem;
        border: 0;
        background: transparent;
        text-align: left;
        color: var(--bs-body-color);
        font: inherit;
        cursor: pointer;

        &:hover {
            background: rgb(var(--bs-primary-rgb), 0.05);
        }

        &.is-selected {
            color: #1c1e21;
            font-weight: 600;
            background: #e5f6f4;
        }
    }

    .plan-shell-asset {
        padding-left: 2.75rem;
    }

    .plan-shell-child {
        padding-left: 2rem;
    }

    .plan-shell-nested {
        padding-left: 4.5rem;
    }

    .plan-shell-twist {
        width: 0;
        height: 0;
        flex-shrink: 0;
        border-top: 0.35rem solid transparent;
        border-bottom: 0.35rem solid transparent;
        border-left: 0.45rem solid var(--bs-secondary-color);

        &.is-open {
            border-left: 0.35rem solid transparent;
            border-right: 0.35rem solid transparent;
            border-top: 0.45rem solid var(--bs-secondary-color);
            border-bottom: 0;
        }
    }

    .plan-shell-icon {
        flex-shrink: 0;
        margin-top: -0.1rem;
    }

    .plan-shell-name {
        min-width: 0;
        padding: 0.45rem 0;
    }

    .plan-shell-main {
        min-width: 0;
        flex: 1;
        display: flex;
        flex-direction: column;
        background: #fff;
    }

    .plan-shell-tabs {
        display: flex;
        align-items: stretch;
        justify-content: center;
        flex-shrink: 0;
        height: 3.4rem;
        margin: 0;
        padding: 0 0.75rem;
        background: var(--bs-primary, #23a89e);
        overflow-x: auto;

        button {
            flex: 0 0 auto;
            align-self: center;
            height: 2.15rem;
            margin: 0;
            padding: 0 0.9rem;
            border: 0;
            border-radius: 0.2rem;
            background: transparent;
            color: #fff;
            font: inherit;
            font-size: 0.98rem;
            font-weight: 500;
            white-space: nowrap;
            cursor: pointer;

            &.is-selected {
                background: color-mix(in srgb, var(--bs-primary, #23a89e) 68%, black);
            }
        }
    }

    .plan-shell-body {
        padding: 1.25rem 1.5rem 1.5rem;
        overflow: auto;
    }

    .plan-shell-title {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 0.6rem;
        margin-bottom: 1rem;

        h3 {
            margin: 0;
            font-size: 1.55rem;
            font-weight: 600;
            line-height: 1.2;
        }
    }

    .plan-shell-type,
    .plan-shell-meta {
        color: var(--bs-secondary-color);
    }

    .plan-shell-heading {
        margin: 1.25rem 0 0.35rem;
        font-size: 1.2rem;
        font-weight: 600;
    }

    .plan-shell-line {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        padding: 0.55rem 0;
        border-bottom: 1px solid var(--bs-border-color);
        font-size: 1rem;
    }

    .plan-shell-well {
        display: block;
    }

    .plan-shell-line-main {
        display: flex;
        align-items: center;
        gap: 0.6rem;
    }

    .plan-shell-db {
        margin-left: 1.85rem;
        color: var(--bs-secondary-color);
        font-size: 0.85rem;
        line-height: 1.45;
    }
</style>
